import { SESSION_END_REASON } from './types';
import type { FlamioConfig, Uploader } from './types';
import { getOrCreateVisitorId } from './identity';
import { shouldSample, resetSamplingCache } from './sampling';
import { startRecording } from './recorder';
import { startConsoleCapture } from './console-capture';
import { startNetworkCapture } from './network-capture';
import { captureWebVitals } from './performance';
import { captureScrollDepth } from './scroll-capture';
import { createUploader } from './uploader';
import { startNavigationCapture } from './navigation-capture';
import {
  createRouteSnapshotScheduler,
  type RouteSnapshotScheduler,
} from './route-snapshot';
import { startVisibilityCapture } from './visibility-capture';
import { touchVisit } from './visit';
import { isMeaningfulEvent, IDLE_TIMEOUT_MS } from './session-lifecycle';
import { RecordingController } from './recording-controller';
import { onRouteChange } from './history-observer';
import {
  rulesFromConfig,
  emptyRecordingPrivacyRules,
  UrlGate,
  type UrlGateCallbacks,
} from './url-gate';
import type { RecordingPrivacyRules } from './types';
import type { RecordPrivacySelectors } from './privacy';

// Cap localStorage writes from the visit touch; a minute of staleness is
// harmless against the 30-min visit window.
const VISIT_TOUCH_INTERVAL_MS = 60 * 1000;

// Heavy DOM recording pauses once a tab is hidden this long (LogRocket's "away").
const HIDDEN_PAUSE_GRACE_MS = 2 * 60 * 1000;

// DOM interactions that wake a dormant (idle-ended) tab into a fresh session.
const WAKE_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'];

class Flamio {
  private static instance: Flamio | null = null;

  private config: FlamioConfig;
  private uploader: Uploader;
  private stopRecording: (() => void) | null = null;
  private stopConsole: (() => void) | null = null;
  private stopNetwork: (() => void) | null = null;
  private stopVitals: (() => void) | null = null;
  private stopScroll: (() => void) | null = null;
  private stopNavigation: (() => void) | null = null;
  private stopVisibility: (() => void) | null = null;
  private routeSnapshotScheduler: RouteSnapshotScheduler | null = null;
  private beforeUnloadHandler: ((event: Event) => void) | null = null;
  private lastVisitTouchAt = 0;
  private recordingController: RecordingController | null = null;
  private visibilityHandler: (() => void) | null = null;
  private wakeHandler: (() => void) | null = null;
  private dormant = false;
  private shedded = false;
  private debug: boolean;
  private privacyRules: RecordingPrivacyRules = emptyRecordingPrivacyRules();
  private urlGate: UrlGate | null = null;
  private stopUrlGate: (() => void) | null = null;
  // rrweb runs iff BOTH agree: urlRecordable (URL gate) AND controllerActive (tab
  // active, not hidden-paused/idle). See syncRecording.
  private urlRecordable = true;
  private controllerActive = true;
  private blockedLivenessAttached = false;
  private blockedLivenessHandler: (() => void) | null = null;

  private constructor(config: FlamioConfig, uploader: Uploader) {
    this.config = config;
    this.uploader = uploader;
    this.debug = config.debug ?? false;
  }

  static init(config: FlamioConfig): Flamio | null {
    try {
      if (typeof window === 'undefined') return null;

      if (Flamio.instance) {
        Flamio.instance.log('Already initialized');
        return Flamio.instance;
      }

      const sampleRate = config.sampleRate ?? 1.0;
      if (!shouldSample(sampleRate)) {
        return null;
      }

      const visitorId = getOrCreateVisitorId();
      let instance: Flamio | null = null;
      const uploader = createUploader(config, visitorId, () =>
        instance?.handleShed()
      );
      instance = new Flamio(config, uploader);
      Flamio.instance = instance;

      if (config.consent !== 'denied' && config.consent !== 'wait') {
        instance.startCapture();
      }

      instance.setupBeforeUnload();
      instance.log('Initialized');

      return instance;
    } catch {
      return null;
    }
  }

  static consent(status: 'granted' | 'denied'): void {
    try {
      const instance = Flamio.instance;
      if (!instance) return;

      instance.config = { ...instance.config, consent: status };

      if (status === 'granted' && !instance.stopRecording) {
        instance.startCapture();
        instance.log('Consent granted, recording started');
      } else if (status === 'denied') {
        instance.stopCapture();
        instance.uploader.destroy().catch(noop);
        Flamio.instance = null;
        resetSamplingCache();
        instance.log('Consent denied, stopped');
      }
    } catch {
      // noop
    }
  }

  static identify(userId: string, traits?: Record<string, unknown>): void {
    try {
      Flamio.instance?.uploader.setIdentity(userId, traits);
      Flamio.instance?.log('Identity set: ' + userId);
    } catch {
      // noop
    }
  }

  static stop(): void {
    try {
      const instance = Flamio.instance;
      if (!instance) return;

      instance.stopCapture();
      instance.removeBeforeUnload();
      instance.uploader.destroy().catch(noop);
      Flamio.instance = null;
      resetSamplingCache();
      instance.log('Stopped');
    } catch {
      // noop
    }
  }

  private startCapture(): void {
    this.uploader.setVisitId(touchVisit());
    this.lastVisitTouchAt = Date.now();

    this.setupRecordingController();
    // DOM recording is gated by the URL allow/blocklist; the rest of the
    // signals run immediately (they're not URL-gated).
    this.beginGatedRecording();

    this.stopConsole = startConsoleCapture((entry) => {
      try {
        this.uploader.addEvents([
          { type: 'flamio:console', data: entry, timestamp: entry.timestamp },
        ]);
      } catch {
        // noop
      }
    });

    this.stopNetwork = startNetworkCapture((entry) => {
      try {
        this.uploader.addEvents([
          { type: 'flamio:network', data: entry, timestamp: entry.timestamp },
        ]);
      } catch {
        // noop
      }
    }, this.config.endpoint);

    this.stopVitals = captureWebVitals((entry) => {
      try {
        this.uploader.addEvents([
          { type: 'flamio:vital', data: entry, timestamp: entry.timestamp },
        ]);
      } catch {
        // noop
      }
    });

    this.stopScroll = captureScrollDepth((entry) => {
      try {
        this.uploader.addEvents([
          { type: 'flamio:scroll', data: entry, timestamp: entry.timestamp },
        ]);
      } catch {
        // noop
      }
    });

    this.routeSnapshotScheduler = createRouteSnapshotScheduler();
    this.stopNavigation = startNavigationCapture((entry) => {
      try {
        this.uploader.addEvents([
          {
            type: 4,
            data: {
              href: entry.to,
              width: window.innerWidth,
              height: window.innerHeight,
            },
            timestamp: entry.timestamp,
          },
          {
            type: 'flamio:navigation',
            data: entry,
            timestamp: entry.timestamp,
          },
        ]);
        this.routeSnapshotScheduler?.schedule(entry);
      } catch {
        // noop
      }
    });

    this.stopVisibility = startVisibilityCapture((entry) => {
      try {
        this.uploader.addEvents([
          {
            type: 'flamio:visibility',
            data: entry,
            timestamp: entry.timestamp,
          },
        ]);
      } catch {
        // noop
      }
    });
  }

  private stopCapture(): void {
    if (this.stopRecording) {
      this.stopRecording();
      this.stopRecording = null;
    }
    if (this.stopConsole) {
      this.stopConsole();
      this.stopConsole = null;
    }
    if (this.stopNetwork) {
      this.stopNetwork();
      this.stopNetwork = null;
    }
    if (this.stopVitals) {
      this.stopVitals();
      this.stopVitals = null;
    }
    if (this.stopScroll) {
      this.stopScroll();
      this.stopScroll = null;
    }
    if (this.stopNavigation) {
      this.stopNavigation();
      this.stopNavigation = null;
    }
    if (this.routeSnapshotScheduler) {
      this.routeSnapshotScheduler.cancel();
      this.routeSnapshotScheduler = null;
    }
    if (this.stopVisibility) {
      this.stopVisibility();
      this.stopVisibility = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.stopUrlGate) {
      this.stopUrlGate();
      this.stopUrlGate = null;
    }
    this.urlGate = null;
    this.detachWakeListeners();
    this.detachBlockedLiveness();
    this.recordingController?.dispose();
    this.recordingController = null;
  }

  // From startCapture (init / consent-grant); wake() re-uses the installed gate.
  private beginGatedRecording(): void {
    this.privacyRules = rulesFromConfig(this.config);
    this.controllerActive = true;
    this.installUrlGate();
    this.syncRecording();
  }

  private privacySelectors(): RecordPrivacySelectors {
    return {
      maskSelectors: this.privacyRules.maskSelectors,
      blockSelectors: this.privacyRules.blockSelectors,
    };
  }

  private installUrlGate(): void {
    const callbacks: UrlGateCallbacks = {
      enter: () => {
        this.urlRecordable = true;
        this.syncRecording();
      },
      leave: () => {
        this.urlRecordable = false;
        this.syncRecording();
      },
    };
    this.urlGate = new UrlGate(
      this.privacyRules,
      callbacks,
      window.location.href
    );
    this.urlRecordable = this.urlGate.isRecordable();
    this.stopUrlGate = onRouteChange(() =>
      this.urlGate?.update(window.location.href)
    );
  }

  // Single owner of rrweb start/stop: record iff the URL gate allows it AND the
  // controller wants the tab active — so the two drivers can't fight (start what
  // the other paused, or start on a hidden/dormant tab).
  private syncRecording(): void {
    const shouldRecord = this.urlRecordable && this.controllerActive;
    if (shouldRecord && !this.stopRecording) this.startDomRecording();
    else if (!shouldRecord && this.stopRecording) this.pauseDomRecording();
    this.syncBlockedLiveness();
  }

  // On a URL-blocked page the user is still on, rrweb is off so the idle timer
  // loses its heartbeat and would falsely seal the session. Feed it real
  // interaction for exactly that window (blocked but active).
  private syncBlockedLiveness(): void {
    const needed = !this.urlRecordable && this.controllerActive;
    if (needed && !this.blockedLivenessAttached) {
      this.blockedLivenessAttached = true;
      this.blockedLivenessHandler = () =>
        this.recordingController?.notifyInteraction();
      this.addInteractionListeners(this.blockedLivenessHandler);
    } else if (!needed) {
      this.detachBlockedLiveness();
    }
  }

  private detachBlockedLiveness(): void {
    if (!this.blockedLivenessAttached) return;
    this.blockedLivenessAttached = false;
    if (this.blockedLivenessHandler) {
      this.removeInteractionListeners(this.blockedLivenessHandler);
      this.blockedLivenessHandler = null;
    }
  }

  private startDomRecording(): void {
    if (this.stopRecording) return;
    this.stopRecording = startRecording(
      this.config,
      this.privacySelectors(),
      (event) => {
        try {
          this.uploader.addEvents([event]);
          if (isMeaningfulEvent(event)) {
            this.recordingController?.notifyInteraction();
          }
        } catch {
          // noop
        }
      }
    );
  }

  // Drives pause (hidden) / dormant (idle); a dormant tab woken by interaction
  // rotates to a fresh session so the ended session/visit stays sealed.
  private setupRecordingController(): void {
    this.recordingController = new RecordingController(
      IDLE_TIMEOUT_MS,
      HIDDEN_PAUSE_GRACE_MS,
      {
        pause: () => {
          this.controllerActive = false;
          this.syncRecording();
        },
        resume: () => {
          this.controllerActive = true;
          this.syncRecording();
        },
        endAndSuspend: () => this.endAndSuspend(),
        wake: () => this.wake(),
        activity: () => this.touchVisitThrottled(),
      }
    );
    this.visibilityHandler = () =>
      this.recordingController?.notifyVisibility(document.hidden);
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private pauseDomRecording(): void {
    if (this.stopRecording) {
      this.stopRecording();
      this.stopRecording = null;
    }
  }

  // Idle boundary: seal the session (drop further events) and wait for return.
  private endAndSuspend(): void {
    this.controllerActive = false;
    this.uploader.endSession(SESSION_END_REASON.Idle).catch(noop);
    this.syncRecording();
    this.uploader.suspend();
    this.dormant = true;
    this.attachWakeListeners();
  }

  // Return after idle: a fresh session in the rotated visit, anchored by a new
  // snapshot. The waking interaction isn't recorded (rrweb was off).
  private wake(): void {
    this.detachWakeListeners();
    this.dormant = false;
    this.uploader.rotateSession();
    this.uploader.setVisitId(touchVisit());
    this.lastVisitTouchAt = Date.now();
    this.controllerActive = true;
    this.syncRecording();
  }

  // Server turned this visitor away over the project's concurrency cap: stop all
  // capture for this page load. The uploader has already gone quiet.
  private handleShed(): void {
    if (this.shedded) return;
    this.shedded = true;
    this.stopCapture();
    this.removeBeforeUnload();
    this.log('Concurrency limit reached — recording stopped for this page');
  }

  private touchVisitThrottled(): void {
    const now = Date.now();
    if (now - this.lastVisitTouchAt >= VISIT_TOUCH_INTERVAL_MS) {
      this.lastVisitTouchAt = now;
      this.uploader.setVisitId(touchVisit(now));
    }
  }

  // Shared by the wake and blocked-liveness paths so their attach/detach stay in sync.
  private addInteractionListeners(handler: () => void): void {
    for (const event of WAKE_EVENTS) {
      window.addEventListener(event, handler, { passive: true, capture: true });
    }
  }

  private removeInteractionListeners(handler: () => void): void {
    for (const event of WAKE_EVENTS) {
      window.removeEventListener(event, handler, { capture: true });
    }
  }

  private attachWakeListeners(): void {
    this.wakeHandler = () => this.recordingController?.notifyInteraction();
    this.addInteractionListeners(this.wakeHandler);
  }

  private detachWakeListeners(): void {
    if (!this.wakeHandler) return;
    this.removeInteractionListeners(this.wakeHandler);
    this.wakeHandler = null;
  }

  private setupBeforeUnload(): void {
    // Real exit ends the session; a bfcache hide (persisted) may restore, so just
    // flush. keepalive lets the final batch survive unload.
    this.beforeUnloadHandler = (event: Event) => {
      try {
        // A dormant tab is already sealed — exiting must not reopen it.
        if (this.dormant) return;
        if ((event as PageTransitionEvent).persisted) {
          this.uploader.flush(true).catch(noop);
        } else {
          this.uploader.endSession(SESSION_END_REASON.Exit, true).catch(noop);
        }
      } catch {
        // noop
      }
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('pagehide', this.beforeUnloadHandler);
  }

  private removeBeforeUnload(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      window.removeEventListener('pagehide', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }

  private log(message: string): void {
    if (this.debug) {
      console.log(`[Flamio] ${message}`);
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop(): void {}

export { Flamio };
export type {
  FlamioConfig,
  BatchPayload,
  DeviceInfo,
  ConsoleEntry,
  NetworkEntry,
  VitalEntry,
} from './types';
export type { ScrollDepthEntry } from './scroll-capture';
